import { format as fnsFormat, formatDistanceToNow as fnsFormatDistanceToNow, parseISO } from "date-fns";

/**
 * Safely parse a date value. Returns null if invalid.
 */
export function safeDate(value: unknown): Date | null {
  if (!value) return null;
  let input = value as string | number;
  // ISO date-only strings (YYYY-MM-DD) are parsed as UTC midnight by new Date(),
  // which shifts the date back one day in timezones behind UTC. Noon avoids this.
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    input = input + "T12:00:00";
  }
  const date = new Date(input);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Safely format a date value. Returns fallback if invalid.
 */
export function safeFormat(
  value: unknown,
  formatStr: string,
  fallback = "—",
): string {
  const date = safeDate(value);
  if (!date) return fallback;
  try {
    return fnsFormat(date, formatStr);
  } catch {
    return fallback;
  }
}

/** Format a timestamp as the app-standard date + time, e.g. "Jun 29, 2026 11:45 AM". */
export function formatDateTime(value: unknown, fallback = "—"): string {
  return safeFormat(value, "MMM d, yyyy h:mm a", fallback);
}

/**
 * Safely format distance to now. Returns fallback if invalid.
 */
export function safeFormatDistanceToNow(
  value: unknown,
  options?: { addSuffix?: boolean },
  fallback = "—",
): string {
  const date = safeDate(value);
  if (!date) return fallback;
  try {
    return fnsFormatDistanceToNow(date, options);
  } catch {
    return fallback;
  }
}

export function isBeforeDate(isoDate: string): boolean {
  return Date.now() < parseISO(isoDate).getTime();
}
